import { variants } from '@eti/components';
import { css, cx } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import { breakpoints, Button, Inline, LinkButton, vars } from '@etg/wings';
import { useScrollTo } from '@eti/utils';
import { getSeatCharacteristics } from '../utils/segmentUtils';
import { SEAT_MAP_UNBOOKED_SEGMENTS_DIALOG_ID } from '../constants/modal';
import { focusOnFirstCharacteristicButton } from '../utils/focusUtils';
import SeatCharacteristicsToggleButton from './characteristics/SeatCharacteristicsToggleButton';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const footerButtonStyles = css`
  flex: 1;

  @media (min-width: ${breakpoints._768}) {
    flex: initial;
    min-width: 120px;
  }
`;
const footerButtonWrapperStyles = {
  base: css`
    position: relative;
    z-index: 1;
  `,
  justifyContent: {
    flexEnd: css`
      justify-content: flex-end;
    `,
    spaceBetween: css`
      justify-content: space-between;
    `
  }
};
const continueLabelPlaceholderStyles = css`
  display: block;
  font-size: 0.875rem;
  font-weight: ${vars.button.fontWeight};
  min-width: 120px;
  padding: 0 12px;
  visibility: hidden;

  @media (max-width: ${breakpoints._768}) {
    display: none;
  }
`;
const skipButtonWrapperStyles = {
  base: css`
    bottom: 0;
    height: 64px;
    justify-content: center;
    left: 0;
    padding: 8px 16px;
    pointer-events: none;
    position: absolute;
    right: 0;
    transition: opacity 0.2s ease-in-out;
    width: 100%;
    z-index: 0;

    @media (min-width: 769px) {
      justify-content: flex-end;
    }
  `,
  hidden: css`
    opacity: 0;
  `
};
const skipButtonStyles = css`
  outline: 2px solid ${vars.colors.focusRing};
  outline-offset: 2px;
`;
const seatMapFooterStyles = {
  base: css`
    background: ${vars.colors.surface.primary};
    bottom: 0;
    box-shadow: 0 -2px 8px 0 #0000000d;
    padding: 8px 16px;
    position: sticky;
    transition: padding-bottom 0.2s ease;
    width: 100%;
  `,
  withPaddingBottom: css`
    @media (max-width: ${breakpoints._768}) {
      padding-bottom: 64px;
    }
  `
};
const SeatMapFooter = ({
  activeSegment,
  canTriggerUnbookedSegmentsDialog,
  isFinalSelectionStep,
  isFirstSegment,
  isPostBooking,
  isSkipButtonShown,
  isUnbookedSegmentsDialogOpen,
  onClose,
  onPreviousSegmentClick,
  onSaveAndContinue,
  onSeatConfirmation,
  segments,
  selectedSeats,
  selectNoOption,
  shouldRenderInline,
  shouldShowSeatCharacteristicsButton,
  shouldShowSummary,
  totalPrice
}) => {
  const {
    scrollVertical
  } = useScrollTo();
  const {
    t
  } = useTranslation();
  const saveAndContinueLabel = isFinalSelectionStep ? t('Product.Bundle.Confirm.Button.Text') : t('Product.SeatMap.Button.Continue');
  const seatCharacteristics = getSeatCharacteristics(segments !== null && segments !== void 0 ? segments : [], activeSegment !== null && activeSegment !== void 0 ? activeSegment : '');
  const segmentBackLabel = t('Button.Text.Back');
  const handleNext = event => {
    if (!shouldShowSummary) {
      onSaveAndContinue === null || onSaveAndContinue === void 0 || onSaveAndContinue(event);
      return;
    }
    if (totalPrice === 0 && isPostBooking) {
      selectNoOption();
      return;
    }
    onClose(selectedSeats);
  };
  const handleSaveAndContinue = event => {
    if (!shouldShowSummary) {
      scrollVertical('smooth', 0);
      onSaveAndContinue === null || onSaveAndContinue === void 0 || onSaveAndContinue(event);
      return;
    }
    onSeatConfirmation === null || onSeatConfirmation === void 0 || onSeatConfirmation(selectedSeats);
  };

  /**
   * Focus is moved to the first traveler by the navigation hook once a segment change has
   * actually happened. Doing it from here focused a traveler even when the click opens the
   * unbooked segments dialog instead, which both scrolled the traveler list away from the
   * active card and left that card as the dialog's focus-restore target.
   */
  const handleOnNextSegmentClick = event => {
    if (shouldRenderInline) {
      handleSaveAndContinue(event);
    } else {
      handleNext(event);
    }
  };
  const handleOnPreviousSegmentClick = event => {
    if (shouldRenderInline) {
      scrollVertical('smooth', 0);
    }
    onPreviousSegmentClick(event);
  };
  return /*#__PURE__*/_jsxs("footer", {
    className: cx(seatMapFooterStyles.base, {
      [seatMapFooterStyles.withPaddingBottom]: isSkipButtonShown
    }),
    "data-testid": "seatMapFooter",
    children: [shouldShowSeatCharacteristicsButton && /*#__PURE__*/_jsx(SeatCharacteristicsToggleButton, {
      seatCharacteristics: seatCharacteristics,
      isSkipButtonShown: isSkipButtonShown !== null && isSkipButtonShown !== void 0 ? isSkipButtonShown : false
    }), /*#__PURE__*/_jsx(Inline, {
      className: cx(footerButtonWrapperStyles.base, {
        [footerButtonWrapperStyles.justifyContent.flexEnd]: isFirstSegment,
        [footerButtonWrapperStyles.justifyContent.spaceBetween]: !isFirstSegment
      }),
      children: /*#__PURE__*/_jsxs(_Fragment, {
        children: [!isFirstSegment && /*#__PURE__*/_jsx(Button, {
          className: footerButtonStyles,
          "data-testid": "segment-back-button",
          onClick: handleOnPreviousSegmentClick,
          variant: variants.SECONDARY,
          children: segmentBackLabel
        }), /*#__PURE__*/_jsx(Button, {
          ...(canTriggerUnbookedSegmentsDialog ? {
            'aria-controls': SEAT_MAP_UNBOOKED_SEGMENTS_DIALOG_ID,
            'aria-expanded': isUnbookedSegmentsDialogOpen,
            'aria-haspopup': 'dialog'
          } : {}),
          className: footerButtonStyles,
          "data-testid": "saveAndContinue-button",
          id: "saveAndContinue-button",
          onClick: handleOnNextSegmentClick,
          variant: variants.PRIMARY,
          children: saveAndContinueLabel
        })]
      })
    }), /*#__PURE__*/_jsxs(Inline, {
      alignY: "center",
      className: cx(skipButtonWrapperStyles.base, {
        [skipButtonWrapperStyles.hidden]: !isSkipButtonShown
      }),
      noWrap: true,
      spacing: 16,
      children: [/*#__PURE__*/_jsx(LinkButton, {
        "aria-hidden": "true",
        className: skipButtonStyles,
        onClick: focusOnFirstCharacteristicButton,
        tabIndex: -1,
        children: t('Product.SeatMap.Accessibility.SkipToSeatMapLegend.Text')
      }), /*#__PURE__*/_jsx("span", {
        "aria-hidden": "true",
        className: continueLabelPlaceholderStyles,
        role: "presentation",
        tabIndex: -1,
        children: saveAndContinueLabel
      })]
    })]
  });
};
export default SeatMapFooter;
//# sourceMappingURL=SeatMapFooter.js.map